import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Column,
  useTable,
  useGlobalFilter,
  useFilters,
  TableInstance,
  HeaderGroup,
  ColumnInstance,
  UseSortByColumnProps,
  useSortBy,
} from 'react-table';
import { DefaultColumnFilter } from '../data-tables/helpers/DefaultColumnFilter';
import { createMultiConditionFilter } from '../../utilities/tableFilters';
import './DataTableGrid.scss';

interface DataTableGridProps<T extends Record<string, any>> {
  data: T[];
  columns: Column<T>[];
  searchTerm?: string;
  onRowClick?: (row: T) => void;
  maxHeight?: string;
  enableBorder?: boolean;
  alignCenter?: boolean;
  noRecordsHeight?: string;
  customSearchFilter?: (data: T[], searchTerm: string) => T[];
  disableSort?: boolean;
}

const DataTableGrid = <T extends Record<string, any>>({
  data = [],
  columns,
  searchTerm = '',
  onRowClick,
  maxHeight,
  enableBorder = true,
  alignCenter = false,
  noRecordsHeight = '250px',
  customSearchFilter,
  disableSort = false,
}: DataTableGridProps<T>) => {
  const tableData = useMemo(() => data || [], [data]);
  const [currentFilters, setCurrentFilters] = useState<any[]>([]);
  const currentFiltersRef = useRef(currentFilters);

  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  const multiConditionFilter = useMemo(() => createMultiConditionFilter(setCurrentFilters, currentFiltersRef), []);

  const defaultColumn: any = useMemo(
    () => ({
      Filter: (props: any) => (
        <DefaultColumnFilter
          {...props}
          clearFilterForColumn={clearFilterForColumn}
          currentFiltersRef={currentFiltersRef}
        />
      ),
    }),
    [],
  );

  const tableColumns = useMemo(() => {
    return columns.map((column) => ({
      ...column,
      filter: (column as any).filter || multiConditionFilter,
    }));
  }, [columns, multiConditionFilter]);

  const processedColumns = useMemo(() => {
    if (!disableSort) return tableColumns;

    return tableColumns.map((col: any) => ({
      ...col,
      disableSortBy: true,
    }));
  }, [tableColumns, disableSort]);

  const tableInstance = useTable<T>(
    {
      columns: processedColumns,
      data: tableData,
      defaultColumn,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setGlobalFilter, setAllFilters } =
    tableInstance as TableInstance<T> & {
      setGlobalFilter: (filterValue: string | undefined) => void;
      setAllFilters: (filters: any[]) => void;
    };

  const finalRows = useMemo(() => {
    if (customSearchFilter && searchTerm?.trim()) {
      const filteredData = rows.map((row) => row.original);
      const searchFilteredData = customSearchFilter(filteredData, searchTerm);

      return rows.filter((row) => searchFilteredData.includes(row.original));
    }
    return rows;
  }, [rows, customSearchFilter, searchTerm]);

  const clearFilterForColumn = (columnId: string) => {
    const remainingFilters = currentFiltersRef.current.filter((filter) => filter.columnId !== columnId);
    setCurrentFilters(remainingFilters);

    if (remainingFilters.length === 0) {
      setAllFilters([]);
    } else {
      setAllFilters(
        remainingFilters.map((filter) => ({
          id: filter.columnId,
          value: filter.filterValue,
        })),
      );
    }
  };

  useEffect(() => {
    if (!customSearchFilter) {
      setGlobalFilter(searchTerm);
    } else {
      setGlobalFilter('');
    }
  }, [searchTerm, setGlobalFilter, customSearchFilter]);

  return (
    <section className='table-container-wrapper'>
      <div
        className={`table-container recommendations-table-container ${alignCenter ? 'common-data-table-grid' : ''} ${!enableBorder ? 'common-data-table-grid-no-border' : ''}`}
        style={{ height: 'unset', maxHeight: `${maxHeight ? maxHeight : '70%'}` }}
      >
        <table {...getTableProps()} className='data-table'>
          <thead>
            {headerGroups.map((headerGroup: HeaderGroup<T>, groupIdx: number) => (
              <tr
                {...headerGroup.getHeaderGroupProps()}
                className={groupIdx === 0 ? 'recommendations-grouped-header-row' : ''}
              >
                {headerGroup.headers.map((col) => {
                  const column = col as unknown as ColumnInstance<T> & UseSortByColumnProps<T> as any;
                  return (
                    <th
                      {...column.getHeaderProps(disableSort ? undefined : column.getSortByToggleProps())}
                      className={`h-[44px] pt-[16px] pb-[16px] pl-[12px] pr-[12px] border-t border-[#4a5463] text-left ${column.id}-column`}
                      style={{
                        ...(column.columns && column.columns.length > 0
                          ? {
                              borderLeft: '2px solid #5d6675',
                              borderRight: '2px solid #5d6675',
                            }
                          : {}),
                      }}
                    >
                      <div
                        className='flex gap-[4px] items-center frequency-table-header'
                        style={{
                          ...(column.columns && column.columns.length > 0
                            ? {
                                justifyContent: 'center',
                                width: '100%',
                              }
                            : {}),
                        }}
                      >
                        <span className='text-[12px] font-semibold text-[#f7f9f9]'>{column.render('Header')}</span>
                        {column.isSorted && (
                          <span
                            className={`freq-sort-icon ${
                              column.isSorted ? (column.isSortedDesc ? 'desc' : 'asc') : 'default'
                            }`}
                            style={{ display: column.render('Header') ? 'inline-block' : 'none' }}
                          />
                        )}
                        {column.canFilter && !column.columns ? <span>{column.render('Filter')}</span> : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {finalRows.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  className={onRowClick ? 'cursor-pointer hover:bg-gray-700' : ''}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      className='text-left p-3'
                      style={{
                        textAlign: `${alignCenter ? 'center' : 'left'}`,
                        ...(cell.column.id === 'diagnosis-recommendation'
                          ? { display: 'flex', justifyContent: 'center' }
                          : {}),
                      }}
                    >
                      {cell.render('Cell')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {finalRows.length === 0 && (
          <div
            className='no-matching-records-container text-center p-4 pl-6 pr-6 flex items-center justify-center'
            style={{ height: noRecordsHeight }}
          >
            <span className='no-matching-records-message text-[#f7f9f9]'>No records available</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default DataTableGrid;
